import type { MeetingOfferStatus } from "@care-by-ella/contracts";

// TES-1168 — the pure parts of "Boka nytt vårdmöte": the clinic's wall-clock
// time as an instant, the price line and the offer's status in Swedish.

const CLINIC_ZONE = "Europe/Stockholm";

function wallClockInZone(instant: number, zone: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(instant));
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? "0");
  return Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
  );
}

// The date and time the clinician typed are Swedish time, whatever zone the
// browser runs in: the meeting is at 10:00 in Stockholm, not at 10:00 where
// the laptop happens to be. Null for anything that is not a real date and
// time.
export function clinicStartIso(date: string, time: string): string | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const t = /^(\d{2}):(\d{2})$/.exec(time);
  if (!d || !t) return null;
  const [y, mo, da] = [Number(d[1]), Number(d[2]), Number(d[3])];
  const [h, mi] = [Number(t[1]), Number(t[2])];
  if (mo < 1 || mo > 12 || da < 1 || da > 31 || h > 23 || mi > 59) return null;
  const wall = Date.UTC(y, mo - 1, da, h, mi);
  // Two passes settle the offset, also across a DST shift.
  let guess = wall;
  for (let i = 0; i < 2; i++) {
    guess += wall - wallClockInZone(guess, CLINIC_ZONE);
  }
  if (wallClockInZone(guess, CLINIC_ZONE) !== wall) return null;
  return new Date(guess).toISOString();
}

export function priceText(amountMinor: number, currency: string): string {
  const major = amountMinor / 100;
  const amount = Number.isInteger(major) ? String(major) : major.toFixed(2);
  return currency.toLowerCase() === "sek"
    ? `${amount} kr`
    : `${amount} ${currency.toUpperCase()}`;
}

export const MEETING_OFFER_STATUS_LABEL: Record<MeetingOfferStatus, string> = {
  awaiting_payment: "Väntar på patientens betalning",
  booked: "Betalt och bokat",
  cancelled: "Återkallat",
  declined: "Avböjd av patienten",
  expired: "Ej betalt i tid",
};

// TES-1219 — who may use "Boka nytt vårdmöte" on the patient page she has
// open. Ö-396: the doctor and the admin book, the coach never does. The API
// (`POST patients/:id/meetings`) admits `doctor` + ADMIN_ROLES, and an admin
// reaches a patient only on a support access basis, so:
//   - "book": the doctor, and the admin in her support view of either the
//     doctor's or the coach's page (the reason is given before it mounts);
//   - "book_on_open": a coach who also holds the admin grant (TES-1089), on
//     her own coach page. The booking routes run her as the admin
//     (RolesGuard), so they need today's support reason for the patient;
//     it is asked for when she opens the form, never merely for opening a
//     patient she coaches;
//   - "none": everyone else, the plain coach included.
export type CareMeetingBookingAccess = "book" | "book_on_open" | "none";

export function careMeetingBookingAccess(input: {
  role: string | undefined;
  roles: readonly string[] | undefined;
  inSupportView: boolean;
}): CareMeetingBookingAccess {
  if (input.inSupportView) return "book";
  if (input.role === "doctor" || input.role === "admin") return "book";
  if (input.role === "coach" && input.roles?.includes("admin")) {
    return "book_on_open";
  }
  return "none";
}
